import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { Loader2 } from 'lucide-react';

export function BillingUsage() {
  const { tenant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sharedKey, setSharedKey] = useState('');
  const [mySharedKey, setMySharedKey] = useState<string | null>(null);
  const [parentUsername, setParentUsername] = useState<string | null>(null);
  const [billingPlans, setBillingPlans] = useState<any[]>([]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment_cancelled')) {
      alert("Payment was cancelled.");
      window.history.replaceState({}, '', '/billing');
    }
    loadSharedKeyInfo();
    loadBillingPlans();
  }, []);

  const loadSharedKeyInfo = async () => {
    try {
      const res = await api.getSharedKeyInfo();
      if (res) {
        setMySharedKey(res.my_key);
        setParentUsername(res.parent_username);
      }
    } catch (e) {
      console.error("Could not load shared key info:", e);
    }
  };

  const loadBillingPlans = async () => {
    try {
      const res = await api.getPublicBillingPlans();
      if (Array.isArray(res)) setBillingPlans(res);
    } catch(e) {
      console.error("Could not load billing plans:", e);
    }
  };

  const handleCheckout = async (packId: string, priceAmount: number, tokensToAdd: number, planName: string) => {
    try {
      setLoading(true);
      const res = await api.createCheckoutSession({ packId, priceAmount, tokensToAdd, planName });
      if (res && res.url) {
        window.location.href = res.url;
      }
    } catch (e: any) {
      if (e.message && e.message.includes('Stripe_Not_Configured')) {
        alert("Tính năng thanh toán đang bảo trì để chờ kết nối Cổng Stripe. Tạm thời bạn có thể liên hệ Admin để nạp token nhé!");
      } else {
        alert("Error initiating checkout: " + e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    try {
      setLoading(true);
      const res = await api.generateSharedKey();
      if (res && res.shared_token_key) {
        setMySharedKey(res.shared_token_key);
        alert("Đã tạo Shared Token Key thành công! Hãy gửi mã này cho bạn bè.");
      }
    } catch (e: any) {
      alert("Lỗi: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkKey = async () => {
    if (!sharedKey.trim()) return;
    try {
      setLoading(true);
      const res = await api.linkSharedKey(sharedKey.trim());
      if (res && res.success) {
        alert("Liên kết thành công! Bạn đang dùng ví của " + sharedKey);
        await loadSharedKeyInfo();
        setSharedKey('');
      }
    } catch (e: any) {
      alert("Lỗi: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', paddingBottom: '40px' }}>
      
      {/* Page Header */}
      <div className="page-header" style={{ marginBottom: '0', padding: '0', border: 'none', background: 'transparent' }}>
        <div>
          <h1 className="page-title">Billing & Usage</h1>
          <p className="page-subtitle">Manage your API credits, top up your balance, and upgrade your subscription plan.</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid-2">
        <div className="stat-card">
          <div className="stat-card__label">Available Credits</div>
          <div className="stat-card__value" style={{ color: 'var(--accent-cyan)' }}>
            {parentUsername ? "Ví của " + parentUsername : (Math.max(0, tenant?.tokens_balance || 0)).toLocaleString()}
          </div>
          <div className="stat-card__sub">Shared across all your generated API keys</div>
          <div className="stat-card__icon">💰</div>
        </div>

        <div className="stat-card">
          <div className="stat-card__label">Current Plan</div>
          <div className="stat-card__value" style={{ color: 'var(--text-primary)' }}>Free Plan</div>
          <div className="stat-card__sub">Priority access & higher rate limits</div>
          <div className="stat-card__icon">✨</div>
        </div>
      </div>

      {/* Shared Token Key */}
      <div className="card card--accent">
        <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          <span>🔑</span> YOUR SHARED TOKEN KEY
        </div>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Nhập Shared Token Key để dùng chung tài khoản với user trả phí (Paid User). Bạn sẽ được chia sẻ quota tokens và quyền lợi model từ tài khoản đó.
        </div>
        
        {parentUsername ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', padding: '12px 16px', borderRadius: 'var(--radius-sm)' }}>
            <span style={{ color: 'var(--accent-green)', fontSize: '13px', fontWeight: 500 }}>✓ Đã liên kết với tài khoản: <strong>{parentUsername}</strong></span>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '10px' }}>
            <input 
              type="text" 
              className="input"
              placeholder="Nhập Shared Token Key..." 
              value={sharedKey}
              onChange={(e) => setSharedKey(e.target.value)}
              style={{ flex: 1 }}
            />
            <button 
              className="btn btn--primary"
              onClick={handleLinkKey}
              disabled={loading || !sharedKey.trim()}
              style={{ whiteSpace: 'nowrap' }}
            >
              Lưu Token
            </button>
          </div>
        )}

        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Mã chia sẻ của bạn</div>
            <code style={{ fontFamily: 'var(--font-mono)', fontSize: '13px', color: 'var(--accent-primary)', background: 'rgba(124,58,237,0.1)', padding: '4px 8px', borderRadius: '4px' }}>
              {mySharedKey || '—  Chưa tạo mã'}
            </code>
          </div>
          <button 
            className="btn btn--secondary btn--sm"
            onClick={handleGenerateKey}
            disabled={loading || !!parentUsername}
          >
            {mySharedKey ? 'Tạo mã mới' : '+ Tạo mã chia sẻ'}
          </button>
        </div>
      </div>

      {/* Bottom Grid: Top-Up & Upgrade */}
      <div className="grid-2">
        {/* Top-up Packs */}
        <div className="card">
          <div className="card__title">ADD API CREDITS (TOP-UP)</div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
            Purchase token credits on a pay-as-you-go basis. Credits never expire and are consumed automatically by your API keys.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {billingPlans.length === 0 && (
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Chưa có gói nạp nào. Vui lòng thêm từ trang Admin.</div>
            )}
            {billingPlans.map(pack => {
              const isAccent = Number(pack.price) >= 100;
              return (
              <div key={pack.id} style={{ 
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                padding: '12px', borderRadius: 'var(--radius-sm)', 
                border: isAccent ? '1px solid rgba(124,58,237,0.3)' : '1px solid rgba(255,255,255,0.05)',
                background: isAccent ? 'rgba(124,58,237,0.1)' : 'var(--bg-elevated)'
              }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: isAccent ? 'var(--accent-primary)' : 'var(--text-primary)' }}>{pack.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{parseInt(pack.token_credit).toLocaleString()} tokens</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' }}>${Number(pack.price).toFixed(2)}</div>
                  <button 
                    onClick={() => handleCheckout(pack.id, Number(pack.price), parseInt(pack.token_credit), pack.name)} 
                    className="btn btn--secondary btn--sm"
                  >
                    Buy
                  </button>
                </div>
              </div>
            )})}
          </div>
        </div>

        {/* Subscription */}
        <div className="card">
          <div className="card__title">UPGRADE PLAN</div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
            Unlock higher rate limits, priority processing, and advanced AI models for your entire team.
          </div>

          <div style={{ 
            background: 'var(--bg-elevated)', 
            border: '1px solid rgba(255,255,255,0.05)', 
            borderRadius: 'var(--radius-sm)', 
            padding: '20px', 
            marginBottom: '20px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ position: 'absolute', inset: 0, background: 'var(--gradient-glow)', opacity: 0.1, pointerEvents: 'none' }} />
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', position: 'relative', zIndex: 1 }}>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px' }}>Team Plan</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>For professional teams & startups</div>
              </div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)' }}>
                $49<span style={{ fontSize: '14px', fontWeight: 400, color: 'var(--text-muted)' }}>/mo</span>
              </div>
            </div>

            <ul style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px', listStyle: 'none', position: 'relative', zIndex: 1 }}>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                <span style={{ color: 'var(--accent-cyan)', fontWeight: 700 }}>✓</span> 5,000,000 bonus tokens monthly
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                <span style={{ color: 'var(--accent-cyan)', fontWeight: 700 }}>✓</span> Priority API processing queue
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                <span style={{ color: 'var(--accent-cyan)', fontWeight: 700 }}>✓</span> Access to premium models (GPT-4o, Claude 3.5)
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                <span style={{ color: 'var(--accent-cyan)', fontWeight: 700 }}>✓</span> Dedicated technical support
              </li>
            </ul>

            <button 
              onClick={() => handleCheckout('sub_team', 49, 5000000, 'Team Plan')}
              className="btn btn--primary"
              style={{ width: '100%', justifyContent: 'center', position: 'relative', zIndex: 1 }}
            >
              Subscribe to Team
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>
            <span>🔒</span> Secured by <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Stripe</span>
          </div>
        </div>
      </div>

      {loading && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(8,8,22,0.8)', backdropFilter: 'blur(4px)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader2 className="w-8 h-8 animate-spin text-[#06b6d4]" />
        </div>
      )}
    </div>
  );
}
